/**
 * BadgeList — the single, canonical multi-badge renderer.
 *
 * Given a product's canonical `Badge[]` array, renders the badges in priority
 * order with per-surface max + overflow applied as a simple priority slice
 * (see docs/Page-Types-And-Templates.md §5). Every surface consumes the SAME array
 * and only differs by `variant` (which sets size, placement, and max). No
 * surface re-derives badge membership.
 *
 * Variants map 1:1 to the spec's surface table:
 *   - 'card'         → product/collection/search cards   (max 2, sm/md, top-left stack)
 *   - 'hero'         → product page hero image           (max 3, lg, top stack)
 *   - 'mini'         → sidebar mini-card / compact slot   (max 1, sm)
 *   - 'carousel'     → block carousels / brand / blog     (max 1, md, inline)
 *   - 'full'         → product detail page badge row      (all badges, md, inline-wrap)
 *
 * The 'full' variant is the product-page complement to the capped hero overlay:
 * it renders the COMPLETE canonical badge set inline (no priority truncation) so
 * the product detail page never hides a badge that appears on a card elsewhere.
 * It consumes the same canonical Badge[] array as every other surface — only the
 * max/placement differ — so a product's badges stay identical card↔page.
 *
 * Callers may override `max`, `size`, and `placement` for one-off needs, but the
 * defaults encode the canonical per-surface rules so the common path is correct
 * by default.
 */

import { Badge, type BadgeSize } from './badge'
import { sortBadges, type Badge as BadgeData } from './select'

export type BadgeListVariant = 'card' | 'hero' | 'mini' | 'carousel' | 'full'
export type BadgePlacement = 'overlay-tl' | 'overlay-tr' | 'inline'

interface VariantPreset {
  max: number
  size: BadgeSize
  placement: BadgePlacement
  /** gap between stacked badges */
  gap: string
}

// Per-surface defaults from the spec's max/overflow table (§5).
const VARIANT_PRESETS: Record<BadgeListVariant, VariantPreset> = {
  hero: { max: 3, size: 'lg', placement: 'overlay-tl', gap: 'gap-2' },
  card: { max: 2, size: 'md', placement: 'overlay-tl', gap: 'gap-1.5' },
  mini: { max: 1, size: 'sm', placement: 'overlay-tl', gap: 'gap-1' },
  carousel: { max: 1, size: 'md', placement: 'inline', gap: 'gap-1' },
  // 'full' renders the complete set: max = the total number of canonical badge
  // types, so a product never loses a badge on its own detail page. Inline +
  // wrap so the row flows under the title instead of stacking over the image.
  full: { max: 10, size: 'md', placement: 'inline', gap: 'gap-1.5' },
}

// Placement → container positioning classes. 'inline' is non-absolute so it can
// flow inside text/flex rows (carousels, brand pills).
const PLACEMENT_CLASSES: Record<BadgePlacement, string> = {
  'overlay-tl': 'absolute top-2 left-2 z-20 flex flex-col',
  'overlay-tr': 'absolute top-2 right-2 z-20 flex flex-col',
  inline: 'inline-flex flex-wrap items-center',
}

interface BadgeListProps {
  badges: BadgeData[] | null | undefined
  variant?: BadgeListVariant
  /** override the variant's default max badge count */
  max?: number
  /** override the variant's default size */
  size?: BadgeSize
  /** override the variant's default placement */
  placement?: BadgePlacement
  showEmoji?: boolean
  className?: string
}

export function BadgeList({
  badges,
  variant = 'card',
  max,
  size,
  placement,
  showEmoji = true,
  className = '',
}: BadgeListProps) {
  const preset = VARIANT_PRESETS[variant]
  const effectiveMax = max ?? preset.max
  const effectiveSize = size ?? preset.size
  const effectivePlacement = placement ?? preset.placement

  // Canonical ordering + overflow: sort ascending by priority, then slice.
  // Overflow is ALWAYS a priority slice — never a different selection rule.
  const visible = sortBadges(badges).slice(0, effectiveMax)

  if (visible.length === 0) return null

  const containerClass =
    `${PLACEMENT_CLASSES[effectivePlacement]} ${preset.gap} ${className}`.trim()

  return (
    <div className={containerClass}>
      {visible.map((badge) => (
        <Badge
          key={badge.type}
          badge={badge}
          size={effectiveSize}
          showEmoji={showEmoji}
        />
      ))}
    </div>
  )
}
